import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { AppButton } from './AppButton';
interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
}
export function AppModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showCloseButton = true
}: AppModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-76">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200" onClick={onClose} data-id="element-77" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4" data-id="element-78">
        <div className={`relative w-full ${sizeClasses[size]} bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-subtle transform transition-all duration-200 animate-scale-in`} onClick={e => e.stopPropagation()} data-id="element-79">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-dark-subtle" data-id="element-80">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-81">
              {title}
            </h2>
            {showCloseButton && <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-all duration-180" data-id="element-82">
                <X className="w-5 h-5" data-id="element-83" />
              </button>}
          </div>

          {/* Body */}
          <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar" data-id="element-84">
            {children}
          </div>

          {/* Footer */}
          {footer && <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-subtle bg-gray-50 dark:bg-dark-subtle rounded-b-lg" data-id="element-85">
              {footer}
            </div>}
        </div>
      </div>
    </div>;
}